import axios from 'axios';
import { krakenService } from './kraken';
import { binanceusService } from './binanceus';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Define SERVER_URL directly since it's not in a constants file
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001/strategiz-io/us-central1/api';

/**
 * Service for managing connections to various exchanges and brokerages
 */
class ExchangeConnectorService {
  private connections: Record<string, any> = {};
  private connectionStatus: Record<string, boolean> = {};
  private initialized = false;

  private initializerMap: Record<string, (credentials: any, userId: string) => Promise<boolean>> = {
    kraken: this.initializeKrakenConnection.bind(this),
    binanceus: this.initializeBinanceUSConnection.bind(this),
    coinbase: this.initializeCoinbaseConnection.bind(this),
    alpaca: this.initializeAlpacaConnection.bind(this),
    tdameritrade: this.initializeTDAmeritradeConnection.bind(this)
  };

  /**
   * Initialize connections to all configured exchanges for a user
   * @param userId The user ID to initialize connections for
   */
  async initializeAllConnections(userId: string): Promise<Record<string, boolean>> {
    console.log(`Initializing all connections for user: ${userId}`);
    
    try {
      this.initialized = true;
      
      // Reset connection status
      this.connectionStatus = {
        kraken: false,
        binanceus: false,
        coinbase: false,
        alpaca: false,
        tdameritrade: false
      };
      
      // Check for API keys in the api_credentials subcollection first
      console.log('Checking api_credentials subcollection...');
      const apiCredentialsRef = collection(db, `users/${userId}/api_credentials`);
      const apiCredentialsDocs = await getDocs(apiCredentialsRef);
      
      let foundCredentials = false;
      
      if (!apiCredentialsDocs.empty) {
        console.log(`Found ${apiCredentialsDocs.size} credentials in api_credentials subcollection`);
        foundCredentials = true;
        
        // Initialize each exchange with credentials from the subcollection
        for (const doc of apiCredentialsDocs.docs) {
          const exchange = doc.id;
          const credentials = doc.data();
          console.log(`Found credentials for ${exchange} in api_credentials subcollection`);
          
          if (this.initializerMap[exchange]) {
            try {
              const result = await this.initializerMap[exchange](credentials, userId);
              this.connectionStatus[exchange] = result;
              console.log(`Initialized ${exchange} connection from api_credentials: ${result}`);
            } catch (error) {
              console.error(`Error initializing ${exchange} connection from api_credentials:`, error);
              this.connectionStatus[exchange] = false;
            }
          }
        }
      } else {
        console.log('No credentials found in api_credentials subcollection, checking legacy locations');
      }
      
      // If no credentials were found in the subcollection, check legacy locations
      if (!foundCredentials) {
        // Check Kraken
        try {
          console.log('Checking Kraken config...');
          const krakenConfigRef = doc(db, 'kraken_config', userId);
          const krakenConfigDoc = await getDoc(krakenConfigRef);
          
          if (krakenConfigDoc.exists()) {
            const krakenConfig = krakenConfigDoc.data();
            console.log('Found Kraken config, initializing connection...');
            const krakenResult = await this.initializeKrakenConnection(krakenConfig, userId);
            this.connectionStatus.kraken = krakenResult;
            console.log(`Initialized Kraken connection: ${krakenResult}`);
          } else {
            console.log('No Kraken config found');
          }
        } catch (error) {
          console.error('Error initializing Kraken connection:', error);
        }
        
        // Check Binance US
        try {
          console.log('Checking Binance US config...');
          const binanceusConfigRef = doc(db, 'binanceus_config', userId);
          const binanceusConfigDoc = await getDoc(binanceusConfigRef);
          
          if (binanceusConfigDoc.exists()) {
            const binanceusConfig = binanceusConfigDoc.data();
            console.log('Found Binance US config, initializing connection...');
            const binanceusResult = await this.initializeBinanceUSConnection(binanceusConfig, userId);
            this.connectionStatus.binanceus = binanceusResult;
            console.log(`Initialized Binance US connection: ${binanceusResult}`);
          } else {
            console.log('No Binance US config found');
          }
        } catch (error) {
          console.error('Error initializing Binance US connection:', error);
        }
        
        // Check user preferences for API keys
        try {
          console.log('Checking user preferences for API keys...');
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const apiKeys = userData.preferences?.apiKeys || {};
            
            for (const [exchange, credentials] of Object.entries(apiKeys)) {
              if (this.initializerMap[exchange]) {
                console.log(`Found ${exchange} credentials in user preferences, initializing connection...`);
                try {
                  const result = await this.initializerMap[exchange](credentials, userId);
                  this.connectionStatus[exchange] = result;
                  console.log(`Initialized ${exchange} connection from user preferences: ${result}`);
                } catch (error) {
                  console.error(`Error initializing ${exchange} connection from user preferences:`, error);
                }
              }
            }
          } else {
            console.log('No user document found');
          }
        } catch (error) {
          console.error('Error checking user preferences:', error);
        }
      }
      
      console.log('Final connection status after initialization:', this.connectionStatus);
      return this.connectionStatus;
    } catch (error) {
      console.error('Error initializing connections:', error);
      return this.connectionStatus;
    }
  }
  
  /**
   * Initialize Kraken connection
   * @param credentials Kraken API credentials
   * @param userId User ID
   */
  async initializeKrakenConnection(credentials: any, userId: string): Promise<boolean> {
    try {
      console.log('Initializing Kraken connection...');
      const kraken = krakenService.createInstance({
        apiKey: credentials.apiKey,
        privateKey: credentials.privateKey
      });
      
      // Test the connection
      await kraken.testConnection();
      this.connections.kraken = kraken;
      console.log('Kraken connection initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Kraken connection:', error);
      return false;
    }
  }
  
  /**
   * Initialize Binance US connection
   * @param credentials Binance US API credentials
   * @param userId User ID
   */
  async initializeBinanceUSConnection(credentials: any, userId: string): Promise<boolean> {
    try {
      console.log('Initializing Binance US connection...');
      const binanceus = binanceusService.createInstance({
        apiKey: credentials.apiKey,
        secretKey: credentials.secretKey,
        userId
      });
      
      // Test the connection
      await binanceus.testConnection();
      this.connections.binanceus = binanceus;
      console.log('Binance US connection initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Binance US connection:', error);
      return false;
    }
  }
  
  /**
   * Initialize Coinbase connection
   * @param credentials Coinbase API credentials
   * @param userId User ID
   */
  async initializeCoinbaseConnection(credentials: any, userId: string): Promise<boolean> {
    try {
      console.log('Initializing Coinbase connection...');
      const { coinbaseService } = await import('./coinbase');
      
      // Check for Coinbase config in Firestore
      const coinbaseConfigRef = doc(db, 'coinbase_config', userId);
      const coinbaseConfigDoc = await getDoc(coinbaseConfigRef);
      
      if (coinbaseConfigDoc.exists()) {
        const coinbaseConfig = coinbaseConfigDoc.data();
        
        coinbaseService.initialize({
          apiKey: coinbaseConfig.apiKey,
          apiSecret: coinbaseConfig.apiSecret
        });
        
        // Test connection by fetching accounts
        await coinbaseService.getAccounts();
        this.connections.coinbase = coinbaseService;
        console.log('Coinbase connection initialized successfully');
        return true;
      }
      return false; // Return false if no config exists
    } catch (error) {
      console.error('Failed to initialize Coinbase connection:', error);
      return false;
    }
  }
  
  /**
   * Initialize Alpaca connection
   * @param credentials Alpaca API credentials
   * @param userId User ID
   */
  async initializeAlpacaConnection(credentials: any, userId: string): Promise<boolean> {
    try {
      console.log('Initializing Alpaca connection...');
      const { alpacaService } = await import('./alpaca');
      
      // Test connection by fetching account info
      await alpacaService.getAccount();
      this.connections.alpaca = alpacaService;
      console.log('Alpaca connection initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Alpaca connection:', error);
      return false;
    }
  }
  
  /**
   * Initialize TD Ameritrade connection
   * @param credentials TD Ameritrade API credentials
   * @param userId User ID
   */
  async initializeTDAmeritradeConnection(credentials: any, userId: string): Promise<boolean> {
    try {
      console.log('Initializing TD Ameritrade connection...');
      const { TDAService } = await import('./tdameritrade');
      const tdService = new TDAService({ apiKey: credentials.apiKey });
      
      // Test connection by fetching account info
      await tdService.getAccount(credentials.accountId);
      this.connections.tdameritrade = tdService;
      console.log('TD Ameritrade connection initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize TD Ameritrade connection:', error);
      return false;
    }
  }
  
  /**
   * Get the connection status for all exchanges
   */
  getConnectionStatus(): Record<string, boolean> {
    return this.connectionStatus;
  }
  
  /**
   * Get a specific exchange connection instance
   * @param exchange The exchange name
   */
  getConnection(exchange: string): any {
    return this.connections[exchange];
  }
  
  /**
   * Check if a specific exchange is connected
   * @param exchange The exchange name
   */
  isConnected(exchange: string): boolean {
    return !!this.connectionStatus[exchange];
  }
  
  /**
   * Check if the service has been initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Initialize a single exchange connection after configuration
   * @param exchange The exchange name (e.g., 'binanceus', 'kraken')
   * @param userId The user ID
   * @returns Whether the connection was successfully established
   */
  async initializeSingleConnection(exchange: string, userId: string): Promise<boolean> {
    if (!userId || !exchange) {
      console.error('Cannot initialize exchange connection: Missing user ID or exchange name');
      return false;
    }

    console.log(`Initializing ${exchange} connection for user: ${userId}`);
    
    try {
      // Get API keys directly from Firestore instead of making server requests
      let apiKeys: Record<string, any> = {};
      
      // Get specific exchange config
      try {
        const configRef = doc(db, `${exchange}_config`, userId);
        const configDoc = await getDoc(configRef);
        if (configDoc.exists()) {
          apiKeys[exchange] = configDoc.data();
        }
      } catch (e) {
        console.warn(`Error fetching ${exchange} config:`, e);
      }
      
      // If not found in specific collection, try user preferences
      if (!apiKeys[exchange]) {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.exists() ? userDoc.data() : {};
        const firebaseApiKeys = userData.preferences?.apiKeys || {};
        
        if (firebaseApiKeys[exchange]) {
          apiKeys[exchange] = firebaseApiKeys[exchange];
        }
      }
      
      if (!apiKeys[exchange]) {
        console.warn(`No API keys found for ${exchange}`);
        return false;
      }
      
      // Initialize specific exchange connection based on the exchange parameter
      switch (exchange) {
        case 'kraken':
          if (apiKeys.kraken && apiKeys.kraken.apiKey && apiKeys.kraken.privateKey) {
            try {
              console.log('Initializing Kraken connection...');
              const kraken = krakenService.createInstance({
                apiKey: apiKeys.kraken.apiKey,
                privateKey: apiKeys.kraken.privateKey
              });
              
              // Test the connection
              await kraken.testConnection();
              this.connectionStatus.kraken = true;
              this.connections.kraken = kraken;
              console.log('Kraken connection initialized successfully');
              return true;
            } catch (error) {
              console.error('Failed to initialize Kraken connection:', error);
              this.connectionStatus.kraken = false;
              return false;
            }
          }
          break;
          
        case 'binanceus':
          if (apiKeys.binanceus && apiKeys.binanceus.apiKey && apiKeys.binanceus.secretKey) {
            try {
              console.log('Initializing Binance US connection...');
              const binanceus = binanceusService.createInstance({
                apiKey: apiKeys.binanceus.apiKey,
                secretKey: apiKeys.binanceus.secretKey,
                userId
              });
              
              // Test the connection
              await binanceus.testConnection();
              this.connectionStatus.binanceus = true;
              this.connections.binanceus = binanceus;
              console.log('Binance US connection initialized successfully');
              return true;
            } catch (error) {
              console.error('Failed to initialize Binance US connection:', error);
              this.connectionStatus.binanceus = false;
              return false;
            }
          }
          break;
          
        case 'coinbase':
          if (apiKeys.coinbase) {
            try {
              console.log('Initializing Coinbase connection...');
              const { coinbaseService } = await import('./coinbase');
              
              // Check for Coinbase config in Firestore
              const coinbaseConfigRef = doc(db, 'coinbase_config', userId);
              const coinbaseConfigDoc = await getDoc(coinbaseConfigRef);
              
              if (coinbaseConfigDoc.exists()) {
                const coinbaseConfig = coinbaseConfigDoc.data();
                
                coinbaseService.initialize({
                  apiKey: coinbaseConfig.apiKey,
                  apiSecret: coinbaseConfig.apiSecret
                });
                
                // Test connection by fetching accounts
                await coinbaseService.getAccounts();
                this.connectionStatus.coinbase = true;
                this.connections.coinbase = coinbaseService;
                console.log('Coinbase connection initialized successfully');
                return true;
              }
            } catch (error) {
              console.error('Failed to initialize Coinbase connection:', error);
              this.connectionStatus.coinbase = false;
              return false;
            }
          }
          break;
          
        case 'alpaca':
          if (apiKeys.alpaca) {
            try {
              console.log('Initializing Alpaca connection...');
              const { alpacaService } = await import('./alpaca');
              
              // Test connection by fetching account info
              await alpacaService.getAccount();
              this.connectionStatus.alpaca = true;
              this.connections.alpaca = alpacaService;
              console.log('Alpaca connection initialized successfully');
              return true;
            } catch (error) {
              console.error('Failed to initialize Alpaca connection:', error);
              this.connectionStatus.alpaca = false;
              return false;
            }
          }
          break;
          
        case 'tdameritrade':
          if (apiKeys.tdameritrade) {
            try {
              console.log('Initializing TD Ameritrade connection...');
              const { TDAService } = await import('./tdameritrade');
              const tdService = new TDAService({ apiKey: apiKeys.tdameritrade.apiKey });
              
              // Test connection by fetching account info
              await tdService.getAccount(apiKeys.tdameritrade.accountId);
              this.connectionStatus.tdameritrade = true;
              this.connections.tdameritrade = tdService;
              console.log('TD Ameritrade connection initialized successfully');
              return true;
            } catch (error) {
              console.error('Failed to initialize TD Ameritrade connection:', error);
              this.connectionStatus.tdameritrade = false;
              return false;
            }
          }
          break;
        
        default:
          console.error(`Unsupported exchange: ${exchange}`);
          return false;
      }
      
      console.warn(`No configuration found for ${exchange}`);
      return false;
    } catch (error) {
      console.error(`Error initializing ${exchange} connection:`, error);
      return false;
    }
  }
}

export const exchangeConnectorService = new ExchangeConnectorService();
